import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Instagram } from "lucide-react";
import { EASE, scrollToId } from "./motion";

const MAILTO =
    "mailto:hello@inskinau.com.au?subject=Consultation%20request%20%E2%80%94%20In%20Skin";

const LINKS = [
    { id: "philosophy", label: "Philosophy" },
    { id: "who-we-are", label: "Who We Are" },
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        setTimeout(() => scrollToId(id), open ? 250 : 0);
    };

    const toTop = () => {
        if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.4 });
        else window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <>
            <motion.header
                initial={{ y: -70, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.9, delay: 1.7, ease: EASE }}
                className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
                    scrolled
                        ? "bg-cream/85 backdrop-blur-md border-b border-ink/5"
                        : "bg-transparent border-b border-transparent"
                }`}
            >
                <nav className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 md:px-12">
                    <button
                        data-testid="nav-logo-button"
                        onClick={toTop}
                        className="flex items-baseline gap-2"
                        aria-label="In Skin — back to top"
                    >
                        <span className="font-serif text-[1.7rem] font-medium leading-none text-ink">
                            In&nbsp;Skin
                        </span>
                        <span className="hidden font-mono text-[9px] uppercase tracking-[0.3em] text-clay sm:block">
                            au
                        </span>
                    </button>

                    <div className="hidden items-center gap-9 md:flex">
                        {LINKS.map((l) => (
                            <button
                                key={l.id}
                                data-testid={`nav-${l.id}-link`}
                                onClick={() => go(l.id)}
                                className="link-underline font-mono text-[11px] uppercase tracking-[0.22em] text-earth transition-colors duration-300 hover:text-ink"
                            >
                                {l.label}
                            </button>
                        ))}
                        <button
                            data-testid="nav-explore-store-button"
                            onClick={() => go("store")}
                            className="link-underline font-mono text-[11px] uppercase tracking-[0.22em] text-earth transition-colors duration-300 hover:text-ink"
                        >
                            Our Store
                        </button>
                        <a
                            data-testid="nav-book-online-button"
                            href={MAILTO}
                            className="rounded-full bg-ink px-6 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-earth hover:shadow-lg"
                        >
                            Book Online
                        </a>
                    </div>

                    <button
                        data-testid="nav-mobile-toggle-button"
                        className="text-ink md:hidden"
                        onClick={() => setOpen(true)}
                        aria-label="Open menu"
                    >
                        <Menu size={26} strokeWidth={1.25} />
                    </button>
                </nav>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="nav-mobile-menu"
                        className="fixed inset-0 z-[90] flex flex-col bg-ink text-cream"
                        initial={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
                        animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
                        exit={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
                        transition={{ duration: 0.6, ease: EASE }}
                    >
                        <div className="flex items-center justify-between px-6 py-5">
                            <span className="font-serif text-2xl italic">In Skin</span>
                            <button
                                data-testid="nav-mobile-close-button"
                                onClick={() => setOpen(false)}
                                aria-label="Close menu"
                            >
                                <X size={28} strokeWidth={1.25} />
                            </button>
                        </div>
                        <div className="flex flex-1 flex-col justify-center gap-1 px-8">
                            {LINKS.map((l, i) => (
                                <motion.button
                                    key={l.id}
                                    data-testid={`nav-mobile-${l.id}-link`}
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.15 + i * 0.07, duration: 0.6, ease: EASE }}
                                    onClick={() => go(l.id)}
                                    className="py-2 text-left font-serif text-5xl font-light"
                                >
                                    {l.label}
                                </motion.button>
                            ))}
                            <motion.button
                                data-testid="nav-mobile-store-button"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.42, duration: 0.6, ease: EASE }}
                                onClick={() => go("store")}
                                className="py-2 text-left font-serif text-4xl font-light text-sand"
                            >
                                Our Store
                            </motion.button>
                            <motion.a
                                data-testid="nav-mobile-book-button"
                                href={MAILTO}
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.45, duration: 0.6, ease: EASE }}
                                className="mt-8 w-max rounded-full bg-cream px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-ink"
                            >
                                Book Online
                            </motion.a>
                            <a
                                data-testid="nav-mobile-instagram-link"
                                href="https://instagram.com/inskinau"
                                target="_blank"
                                rel="noreferrer"
                                className="mt-6 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-sand"
                            >
                                <Instagram size={14} /> @inskinau
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
